import { Environment } from "../utils/Environment";
import React, { useState, useEffect } from "react";
import { postWithUserTableParams } from "../services/fetchWrapper";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

interface ProcessDistanceRequest {
    job_id: string;
    compare_table_name: string;
    score_column_name: string;
    main_table_name: string;
    layer_name?: string;
    buffer_list: number[];
    score_list: number[];
    and_clause_filter?: string;
    is_srid_feet_clause?: string;
    client_id: string;
}

const RAILWAY_SCORE_COLUMNS = ["cof_rr_bo", "cof_rr_mis", "env_rr_lof", "cof_rr_fr"];


const ProcessRailScore: React.FC = () => {
    const [distanceScoreJsons, setDistanceScoreJsons] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pipeRailColumn, setPipeRailColumn] = useState<string>("");
    const [possibleColumns, setPossibleColumns] = useState<string[]>([]);

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [pollingJobIds, setPollingJobIds] = useState<string[]>([]);
    const [batchPollingActive, setBatchPollingActive] = useState(false);
    const [jobStatusMap, setJobStatusMap] = useState<Record<string, string>>({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [processingDone, setProcessingDone] = useState(false);

    useEffect(() => {
        if (!batchPollingActive || pollingJobIds.length === 0) return;

        // Find incomplete jobs based on their individual status
        let incompleteIds = pollingJobIds.filter(
            id => !["completed", "success", "failed", "unknown", "error"].includes(jobStatusMap[id] ?? "")
        );

        // Sort IDs numerically, smallest to largest
        incompleteIds = incompleteIds.sort((a, b) => Number(a) - Number(b));

        if (incompleteIds.length === 0) {
            if (uploadStatus && !uploadStatus.toLowerCase().includes("fail")) {
                setShowSuccess(true);
                setProcessingDone(true);
                setUploadStatus("complete");
                setStatusMessage("All jobs complete.");
                setPollingJobIds([]);
                setBatchPollingActive(false);
                localStorage.setItem("railScoreDone", "true");
            } else {
                setShowSuccess(false);
                setProcessingDone(true);
                setUploadStatus(uploadStatus);
                setStatusMessage(statusMessage);
                setPollingJobIds([]);
                setBatchPollingActive(false);
                localStorage.setItem("railScoreDone", "true");
            }

            return;
        }

        // Poll one job per interval
        const idToPoll = incompleteIds[0];
        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`${apiUrl}/upload-status/${idToPoll}`);
                if (response.ok) {
                    const data = await response.json();
                    setJobStatusMap(prev => ({
                        ...prev,
                        [idToPoll]: data.upload_status
                    }));
                    setUploadStatus(data.upload_status);
                    setStatusMessage(data.message);
                } else {
                    setStatusMessage("Failed to get upload status.");
                }
            } catch (err) {
                setStatusMessage("Error polling job status.");
            }
        }, 3000); // poll every 3 seconds

        return () => clearTimeout(timer);
    }, [batchPollingActive, pollingJobIds, jobStatusMap, apiUrl]);

    // Fetch client data for scoring
    useEffect(() => {
        const fetchClientData = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            setLoading(true);
            setError(null);
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-client-data-for-distance-scoring`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client data");
                }
                const data = await response.json();
                if (data.status !== 'success' || !Array.isArray(data.jsons)) {
                    throw new Error("Invalid response format from backend");
                }
                // Only keep railway scoring JSONs
                const filtered = data.jsons.filter(
                    (j: any) => RAILWAY_SCORE_COLUMNS.includes((j.score_column_name || "").toLowerCase())
                );
                setDistanceScoreJsons(filtered);
            } catch (err: any) {
                setError(err.message || "Failed to fetch client data");
            } finally {
                setLoading(false);
            }
        };
        fetchClientData();
    }, [apiUrl]);

    // Fetch possible Rail columns
    useEffect(() => {
        const fetchRailColumns = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            setLoading(true);
            setError(null);
            try {
                // You may need to pass job_id, client_id, or table_name as needed by your backend
                const response = await postWithUserTableParams(`${apiUrl}/get-rail-column`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch Rail columns");
                }
                const data = await response.json();
                if (!Array.isArray(data.possible_material_columns)) {
                    throw new Error("Invalid response format for Rail columns");
                }
                const columnNames = data.possible_material_columns.map((col: any) => col.column_name);
                setPossibleColumns(columnNames);
                if (columnNames.length > 0) {
                    setPipeRailColumn(columnNames[0]);
                }
            } catch (err: any) {
                setError(err.message || "Failed to fetch Rail columns");
            } finally {
                setLoading(false);
            }
        };
        fetchRailColumns();
    }, [apiUrl]);

    // ...existing code...
    const handleProcessDistanceScores = async () => {
        // Get values from localStorage at function run time
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        setLoading(true);
        setError(null);
        try {
            // Save customer calc columns first
            const payload = [{
                client_id,
                job_id,
                column_name: pipeRailColumn,
                column_value: "Y",
                flow_name: "railway",
                description: "User confirmed passenger column for railway scoring"
            }];
            const saveResponse = await fetch(`${apiUrl}/save-customer-calc-columns`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ columns: payload, client_id }),
            });
            if (!saveResponse.ok) {
                throw new Error("Failed to save customer calc columns");
            }

            // Build the batch request array
            const batchRequests: ProcessDistanceRequest[] = distanceScoreJsons.map((raw: any) => {
                const column = pipeRailColumn;
                const value = "Y";
                let and_clause_filter = raw.and_clause_filter || "";
                and_clause_filter = and_clause_filter
                    .replace(/{column}/g, column)
                    .replace(/{value}/g, value);

                return {
                    job_id: raw.job_id,
                    compare_table_name: raw.compare_table_name,
                    score_column_name: raw.score_column_name,
                    main_table_name: raw.main_table_name,
                    layer_name: raw.layer_name,
                    buffer_list: raw.buffer_list,
                    score_list: raw.score_list,
                    and_clause_filter,
                    is_srid_feet_clause: raw.is_srid_feet_clause,
                    client_id: raw.client_id
                };
            });

            // Send the batch request
            const response = await fetch(`${apiUrl}/process-distance-score`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(batchRequests),
            });
            if (!response.ok) {
                throw new Error("Failed to process distance scores");
            }
            const data = await response.json();
            setStatusMessage(data.message || "Batch started...");
            setUploadStatus("processing");
            if (Array.isArray(data.ids) && data.ids.length > 0) {
                setPollingJobIds(data.ids);
                setBatchPollingActive(true); // Start polling only if we have job IDs
            } else {
                setStatusMessage("Waiting for jobs to start...");
                setUploadStatus("processing");
                // Optionally, you could show a spinner or message until jobs are available
            }
        } catch (err: any) {
            setError(err.message || "Failed to process distance scores");
        } finally {
            setLoading(false);
        }
    };
    // ...existing code...

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                maxWidth: 500,
                width: "100%",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Typography variant="h5"
                sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>
                Rail Scoring
            </Typography>
            <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Typography variant="h5" sx={{ mb: 3, fontSize: 16 }}>
                The column used for scoring <strong>Rail Type</strong> should be present. If not visible, manually enter column name with information that designates what kind of rail intersects with the asset. Once scoring is complete, a success message will appear.
            </Typography>
            {showSuccess ? (
                <SuccessMessageComponent />
            ) : (
                processingDone && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                        <HighlightOffIcon sx={{ fontSize: 40, color: "#828282" }} />
                        <Typography variant="body2" sx={{ color: "#828282" }}>
                            Scoring did not complete successfully.
                        </Typography>
                    </Box>
                )
            )}
            <Typography variant="body2" sx={{ mb: 3, fontSize: 18, fontWeight: 700, maxWidth: 450, textAlign: "center" }}>
                Rail Type Column
            </Typography>
            {possibleColumns.length > 0 ? (
                <Autocomplete
                    freeSolo
                    options={possibleColumns}
                    value={pipeRailColumn}
                    inputValue={pipeRailColumn}
                    onChange={(_, value) => setPipeRailColumn(value || "")}
                    onInputChange={(_, value) => setPipeRailColumn(value || "")}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#F6FFE8",
                                    height: 36,
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "8px 12px",
                                },
                            }}
                        />
                    )}
                    sx={{
                        mb: 2,
                        width: 300,
                        maxWidth: "100%",
                    }}
                />
            ) : (
                <TextField
                    variant="outlined"
                    value={pipeRailColumn}
                    onChange={e => setPipeRailColumn(e.target.value)}
                    sx={{
                        mb: 2,
                        width: 300,
                        maxWidth: "100%",
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#F6FFE8",
                            height: 36,
                        },
                        "& .MuiOutlinedInput-input": {
                            padding: "8px 12px",
                        },
                    }}
                    fullWidth
                />
            )}
            <Button
                variant="contained"
                color="primary"
                disabled={loading || distanceScoreJsons.length === 0}
                onClick={handleProcessDistanceScores}
                sx={{ backgroundColor: "#0373B5" }}
            >
                {loading ? "Processing..." : "Process Rail scores"}
            </Button>
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        maxWidth: 500,
                        mx: "auto", // centers horizontally
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center", // centers content inside the box
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">
                        Status: {uploadStatus}
                    </Typography>
                    <Typography variant="body2">
                        Message: {statusMessage}
                    </Typography>
                </Box>
            )}
            {batchPollingActive && (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    <Typography variant="body2">Processing, please wait...</Typography>
                </Box>
            )}
            {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
        </Box>
    );
};

export default ProcessRailScore;